import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { useSiteData } from "@/context/SiteDataContext";
import { waLink } from "@/lib/api";
import { leadSent, onceFlag } from "@/lib/attribution";
import { EnquiryForm } from "./EnquiryForm";
import { WhatsAppIcon } from "./WhatsAppIcon";

const SEEN_KEY = "owt_quote_popup_seen";
const DELAY = 25000;

const usePopupTimer = () => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    let timer;
    const attempt = () => {
      if (leadSent()) return;
      if (document.querySelector("[role=dialog]")) { timer = setTimeout(attempt, 5000); return; }
      if (onceFlag(SEEN_KEY)) setShow(true);
    };
    try { if (!localStorage.getItem(SEEN_KEY)) timer = setTimeout(attempt, DELAY); } catch { /* storage blocked */ }
    return () => clearTimeout(timer);
  }, []);
  return [show, setShow];
};

export const FreeQuotePopup = () => {
  const { settings } = useSiteData();
  const { pathname } = useLocation();
  const [show, setShow] = usePopupTimer();
  const [expanded, setExpanded] = useState(false);
  const pkgSlug = pathname.match(/^\/packages\/([^/]+)/)?.[1];
  const { packages } = useSiteData();
  const pkg = pkgSlug && packages.find((p) => p.slug === pkgSlug);
  if (!show) return null;
  return (
    <div role="complementary" aria-label="Free quote" data-testid="quote-popup"
      className="fixed z-50 inset-x-3 bottom-[76px] lg:inset-x-auto lg:right-6 lg:bottom-24 lg:w-[400px] rounded-3xl border border-slate-200 bg-white shadow-2xl shadow-slate-900/20 reveal max-h-[78vh] overflow-y-auto">
      <button type="button" onClick={() => setShow(false)} aria-label="Close" data-testid="quote-popup-close"
        className="absolute right-3 top-3 h-9 w-9 grid place-items-center rounded-full text-slate-500 hover:bg-slate-100"><X className="h-4 w-4" /></button>
      <div className="p-5 sm:p-6">
        <p className="text-[11px] uppercase tracking-[0.25em] font-semibold text-ocean">Free Andaman quote</p>
        <p className="mt-1.5 pr-8 font-display text-2xl text-navy font-semibold leading-tight">{pkg ? `Get a free quote for ${pkg.name}` : "Planning an Andaman trip?"}</p>
        <p className="mt-1.5 text-sm text-slate-600">Share your dates and we'll send a personalised quotation.</p>
        {expanded ? (
          <div className="mt-4"><EnquiryForm compact idPrefix="popup-enquiry" defaultPackageId={pkg?.id || ""} /></div>
        ) : (
          <div className="mt-4 grid grid-cols-2 gap-2">
            <button type="button" onClick={() => setExpanded(true)} data-testid="quote-popup-open-form" className="h-11 rounded-full bg-ocean text-white text-sm font-semibold hover:bg-navy transition-colors">Get Free Quote</button>
            <a href={waLink(settings.whatsapp, "Hi Ocean Wings Travel, I'd like a free quote for an Andaman trip.")} target="_blank" rel="noreferrer" data-testid="quote-popup-whatsapp"
              className="h-11 rounded-full bg-emerald-500 text-white text-sm font-semibold inline-flex items-center justify-center gap-1.5 hover:bg-emerald-600 transition-colors"><WhatsAppIcon className="h-4 w-4" /> WhatsApp</a>
          </div>
        )}
      </div>
    </div>
  );
};
